import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  collectShippingInfo,
  setSelectedRateRedux,
} from "../../../../store/slices/OrderSlice/OrderSlice";
import FreightLoading from "../../../atoms/Loaders/FreightLoading";

export default function Shipping() {
  const dispatch = useDispatch();
  const {
    shippingInfo,
    collectingShippingInfo,
    lastRatedWeight,
    selectedReduxRate,
    shippingInfoError,
    shippingRatingProgress,
    shippingRatingStatus,
  } = useSelector((state) => state.orders);
  const { cart } = useSelector((state) => state.cart);

  const [selectedRate, setSelectedRate] = useState(null);
  const [totalFreightCost, setTotalFreightCost] = useState(0);
  const [showAllRates, setShowAllRates] = useState(false);
  const [showSingleWarehouseRates, setShowSingleWarehouseRates] =
    useState(false);
  const lastCallRef = useRef({ weight: null, hasSelectedRate: null });

  // NOTE: the previous mount-reset effect that wiped lastCallRef here was
  // removed. Under React StrictMode it re-ran on the dev double-invoke and
  // defeated the guard, firing collectShippingInfo twice. On a genuine remount
  // useRef re-initializes on its own, so the reset was redundant anyway.
  // Single-flight is now guaranteed by the thunk's `condition` option.

  useEffect(() => {
    const shouldCall =
      lastRatedWeight !== cart.total_weight || !selectedReduxRate;
    const hasChanged =
      lastCallRef.current.weight !== cart.total_weight ||
      lastCallRef.current.hasSelectedRate !== !!selectedReduxRate;

    if (shouldCall && hasChanged) {
      lastCallRef.current = {
        weight: cart.total_weight,
        hasSelectedRate: !!selectedReduxRate,
      };
      dispatch(collectShippingInfo());
    }
  }, [dispatch, lastRatedWeight, cart.total_weight, selectedReduxRate]);
  // When shippingInfo changes, default to preferred rate if available
  useEffect(() => {
    if (shippingInfo?.preferred_rate) {
      const preferredRates = convertRateToArray(shippingInfo?.preferred_rate);
      if (preferredRates.length > 0) {
        setSelectedRate({
          type: "preferred_rate",
          data:
            preferredRates.length === 1 ? preferredRates[0] : preferredRates,
          isMultiWarehouse: preferredRates.length > 1,
        });
      }
    }
    // Reset view states when new shipping info arrives
    setShowAllRates(false);
    setShowSingleWarehouseRates(false);
  }, [shippingInfo, shippingInfo?.preferred_rate]);

  // Calculate freight cost whenever selection changes
  useEffect(() => {
    if (!selectedRate) {
      setTotalFreightCost(0);
      return;
    }

    let selectedPrice = 0;
    let selectedFees = 0;

    // Handle different rate types from new structure
    if (
      selectedRate.type === "all_rates" ||
      selectedRate.type === "single_warehouse_rates"
    ) {
      // Try to extract price from multiple possible sources
      if (selectedRate.data.total_price && selectedRate.data.total_price > 0) {
        selectedPrice = selectedRate.data.total_price;
      } else {
        // Look in combination_details for price and fees - use FREIGHT_PRE_FEE when available
        const warehouseDetails = Object.values(
          selectedRate.data.combination_details || {},
        )[0];
        if (warehouseDetails) {
          selectedPrice =
            warehouseDetails.FREIGHT_PRE_FEE ||
            warehouseDetails.PRICE ||
            warehouseDetails.price ||
            0;
          selectedFees = warehouseDetails.TOTAL_FEE_AMOUNT || 0;
        }
      }
    } else if (selectedRate.type === "customer_pickup") {
      // Customer pickup is always free
      selectedPrice = 0;
      selectedFees = 0;
    } else {
      // Handle multi-warehouse rates (sum all rates and fees) - use FREIGHT_PRE_FEE when available
      if (selectedRate.isMultiWarehouse && Array.isArray(selectedRate.data)) {
        selectedPrice = selectedRate.data.reduce(
          (total, rate) => total + (rate.FREIGHT_PRE_FEE || rate.PRICE || 0),
          0,
        );
        selectedFees = selectedRate.data.reduce(
          (total, rate) => total + (rate.TOTAL_FEE_AMOUNT || 0),
          0,
        );
      } else {
        selectedPrice =
          selectedRate.data.FREIGHT_PRE_FEE || selectedRate.data.PRICE || 0;
        selectedFees = selectedRate.data.TOTAL_FEE_AMOUNT || 0;
      }
    }

    // Handle free freight logic
    if (shippingInfo?.free_freight) {
      if (
        selectedRate.type === "preferred_rate" ||
        selectedRate.type === "customer_pickup"
      ) {
        // Free freight on preferred rate - customer only pays fees if any
        setTotalFreightCost(selectedFees);
      } else {
        // For other rate types, calculate the difference from the preferred rate plus fees
        let preferredRatePrice = 0;
        if (shippingInfo?.preferred_rate) {
          const preferredRates = convertRateToArray(
            shippingInfo.preferred_rate,
          );
          preferredRatePrice = preferredRates.reduce(
            (total, rate) => total + (rate.FREIGHT_PRE_FEE || rate.PRICE || 0),
            0,
          );
        }
        // Customer pays freight difference + fees
        setTotalFreightCost(
          Math.max(0, selectedPrice - preferredRatePrice) + selectedFees,
        );
      }
    } else {
      // Not free freight - customer pays full freight price (fees included)
      if (selectedRate.type === "customer_pickup") {
        setTotalFreightCost(0);
      } else {
        setTotalFreightCost(selectedPrice);
      }
    }
  }, [selectedRate, shippingInfo?.free_freight, shippingInfo?.preferred_rate]);

  // Helper function to convert nested object structure to array
  const convertRateToArray = (rateObject) => {
    if (!rateObject) return [];

    return Object.entries(rateObject).map(([location, carrierInfo]) => ({
      location,
      ...carrierInfo,
    }));
  };

  const rateCardClasses = (isSelected) =>
    `group w-full rounded-lg border p-4 text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2 ${
      isSelected
        ? "border-[var(--primary-color)] bg-white ring-1 ring-[var(--primary-color)]"
        : "border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50"
    }`;
  const handleRateSelection = (type, rate) => {
    if (type === "preferred_rate") {
      // For preferred rates, get all rates from the preferred_rate object
      const allPreferredRates = convertRateToArray(shippingInfo.preferred_rate);

      // Add total cost to preferred rates for ReviewSubmit
      const enhancedPreferredRates = allPreferredRates.map((rate) => {
        const freightCost = rate.FREIGHT_PRE_FEE || rate.PRICE || 0;
        const fees = rate.TOTAL_FEE_AMOUNT || 0;
        return {
          ...rate,
          TOTAL_COST: freightCost + fees,
        };
      });

      const selectedData = {
        type,
        data:
          enhancedPreferredRates.length === 1
            ? enhancedPreferredRates[0]
            : enhancedPreferredRates,
        isMultiWarehouse: enhancedPreferredRates.length > 1,
      };
      setSelectedRate(selectedData);
      // Also dispatch to Redux for preferred rate
      dispatch(setSelectedRateRedux(selectedData));
    } else if (type === "customer_pickup") {
      // Handle customer pickup selection
      const selectedData = {
        type,
        data: rate,
        isMultiWarehouse: false,
      };
      setSelectedRate(selectedData);
      dispatch(setSelectedRateRedux(selectedData));
    } else {
      const selectedData = { type, data: rate, isMultiWarehouse: false };
      setSelectedRate(selectedData);

      // For alternative rates, we need to include the total cost (freight + fees) for ReviewSubmit
      const warehouseDetails = Object.values(rate.combination_details || {})[0];
      let totalCost = 0;

      if (warehouseDetails) {
        const freightCost =
          warehouseDetails.FREIGHT_PRE_FEE || warehouseDetails.PRICE || 0;
        const fees = warehouseDetails.TOTAL_FEE_AMOUNT || 0;
        totalCost = freightCost + fees;

        // Create enhanced warehouse details with total cost
        const enhancedWarehouseDetails = {
          ...warehouseDetails,
          TOTAL_COST: totalCost, // Add total cost for ReviewSubmit to use
        };

        dispatch(
          setSelectedRateRedux({
            data:
              rate?.combination_details?.length === 1
                ? enhancedWarehouseDetails
                : {
                    ...rate,
                    combination_details: {
                      [Object.keys(rate.combination_details)[0]]:
                        enhancedWarehouseDetails,
                    },
                  },
            type,
            isMultiWarehouse: rate.length > 1,
          }),
        );
      } else {
        dispatch(
          setSelectedRateRedux({
            data:
              rate?.combination_details?.length === 1
                ? rate?.combination_details[0]
                : rate,
            type,
            isMultiWarehouse: rate.length > 1,
          }),
        );
      }
    }
  };
  // Render Customer Pick Up option - always available
  const renderCustomerPickUp = () => {
    const rateType = "customer_pickup";
    const isSelected = selectedRate && selectedRate.type === rateType;

    return (
      <div className="rate-section mb-6">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
          Pickup alternative
        </h3>
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            className={rateCardClasses(isSelected)}
            onClick={() =>
              handleRateSelection(rateType, {
                CARRIER: "CPU",
                PRICE: 0,
                service: "Customer Pick Up",
                NAME: "Customer Pick Up",
              })
            }
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-base font-semibold text-gray-900">
                  Customer pickup
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Collect this order from the assigned warehouse.
                </p>
              </div>
              <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-green-700">
                Free
              </span>
            </div>
            <div className="mt-4 flex items-center gap-2 border-t border-gray-200 pt-3 text-xs text-gray-600">
              <span className="font-semibold uppercase tracking-wide">CPU</span>
              <span aria-hidden="true">·</span>
              <span>Warehouse pickup</span>
            </div>
            {isSelected && (
              <div className="mt-3 text-xs font-semibold text-[var(--primary-color)]">
                ✓ Selected plan
              </div>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderPreferredRate = () => {
    if (!shippingInfo?.preferred_rate) return null;

    const rates = convertRateToArray(shippingInfo.preferred_rate);
    if (rates.length === 0) return null;

    const rateType = "preferred_rate";
    const isSelected = selectedRate && selectedRate.type === rateType;

    // Handle multiple warehouses for preferred rate
    if (rates.length > 1) {
      const totalPrice = rates.reduce(
        (total, rate) => total + (rate.PRICE || 0),
        0,
      );
      const allCarriers = [
        ...new Set(rates.map((rate) => rate.CARRIER || rate.carrier)),
      ].join(", ");
      const avgDays = Math.ceil(
        rates.reduce((total, rate) => total + (rate.DAYS || 3), 0) /
          rates.length,
      );

      return (
        <div className="rate-section mb-6">
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--primary-color)]">
            Recommended freight plan
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              className={rateCardClasses(isSelected)}
              onClick={() => handleRateSelection(rateType, null)}
            >
              <div className="flex items-start justify-between gap-4">
                {!shippingInfo.free_freight && (
                  <>
                    <span className="text-2xl font-semibold tracking-tight text-gray-900">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </>
                )}
                {shippingInfo.free_freight && (
                  <span className="text-2xl font-semibold text-green-700">
                    FREE
                  </span>
                )}
                <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                  {rates.length} warehouse route
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gray-200 pt-3 text-sm text-gray-600">
                <div>Carriers: {allCarriers}</div>
                <div>Estimated Days: {avgDays}</div>
              </div>
              {isSelected && (
                <div className="mt-3 text-xs font-semibold text-[var(--primary-color)]">
                  ✓ Selected plan
                </div>
              )}
            </button>
          </div>
        </div>
      );
    }

    // Single warehouse preferred rate
    const rate = rates[0];
    return (
      <div className="rate-section mb-6">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--primary-color)]">
          Recommended freight plan
        </h3>
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            className={rateCardClasses(isSelected)}
            onClick={() => handleRateSelection(rateType, rate)}
          >
            <div className="flex justify-between">
              {!shippingInfo.free_freight && (
                <span className="text-2xl font-semibold tracking-tight text-gray-900">
                  ${(rate.FREIGHT_PRE_FEE ?? rate.PRICE)?.toFixed(2)}
                </span>
              )}
              {shippingInfo.free_freight && (
                <span className="text-2xl font-semibold text-green-700">
                  FREE
                </span>
              )}
            </div>
            {/* Display fees */}
            {rate.TOTAL_FEE_AMOUNT !== undefined && (
              <div className="text-xs text-gray-600 mt-1">
                {shippingInfo.free_freight
                  ? `Additional Fees: $${rate.TOTAL_FEE_AMOUNT.toFixed(2)}`
                  : `Additional Fees: $${rate.TOTAL_FEE_AMOUNT.toFixed(2)}`}
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gray-200 pt-3 text-sm text-gray-600">
              <div>
                Carrier: {rate.service || rate.NAME} (
                {rate.carrier || rate.CARRIER})
              </div>
              <div>Estimated Days: {rate.DAYS || 3}</div>
            </div>
            {isSelected && (
              <div className="mt-3 text-xs font-semibold text-[var(--primary-color)]">
                ✓ Selected plan
              </div>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Function to render alternative rates (all_rates or single_warehouse_rates)
  const renderAlternativeRates = (rates, rateType, title) => {
    if (!rates || rates.length === 0) return null;

    // Calculate preferred rate price for free freight comparison - use FREIGHT_PRE_FEE when available
    let preferredRatePrice = 0;
    if (shippingInfo?.preferred_rate) {
      const preferredRates = convertRateToArray(shippingInfo.preferred_rate);
      preferredRatePrice = preferredRates.reduce(
        (total, rate) => total + (rate.FREIGHT_PRE_FEE || rate.PRICE || 0),
        0,
      );
    }

    // Filter out preferred rates to ensure they're not duplicated in alternative rates sections
    const filteredRates = rates.filter((rateOption) => {
      // Get the warehouse details for this rate option
      const warehouseDetails = Object.values(
        rateOption.combination_details || {},
      )[0];
      if (!warehouseDetails) return true;

      // If there's no preferred rate, keep all rates
      if (!shippingInfo?.preferred_rate) return true;

      // Extract key identifiers from this rate
      const rateCarrier = warehouseDetails.carrier || warehouseDetails.CARRIER;
      const rateService = warehouseDetails.service || warehouseDetails.NAME;
      const ratePrice = warehouseDetails.price || warehouseDetails.PRICE;
      const rateLocation = warehouseDetails.location;

      // Get the preferred rates as an array for easier comparison
      const preferredRates = convertRateToArray(shippingInfo.preferred_rate);

      // Check if this rate matches any of the preferred rates
      for (const prefRate of preferredRates) {
        // Compare key properties to identify if this is the preferred rate
        if (
          (prefRate.carrier === rateCarrier ||
            prefRate.CARRIER === rateCarrier) &&
          (prefRate.service === rateService || prefRate.NAME === rateService)
        ) {
          // This is likely the preferred rate, so filter it out
          return false;
        }
      }

      // Keep rates that don't match the preferred rate
      return true;
    });

    return (
      <div className="rate-section mb-6">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
          {title}
        </h3>
        <div className="grid grid-cols-1 gap-3">
          {filteredRates.map((rateOption, index) => {
            // Extract carrier and estimated days only
            const warehouseDetails = Object.values(
              rateOption.combination_details,
            )[0];
            const carrier =
              warehouseDetails.carrier ||
              warehouseDetails.CARRIER ||
              "Unknown Carrier";

            const service =
              warehouseDetails.service ||
              warehouseDetails.NAME ||
              "Unknown Service";
            const days =
              warehouseDetails.DAYS || warehouseDetails.delivery || "N/A";

            // Try to extract price from multiple possible sources - use FREIGHT_PRE_FEE when available
            let basePrice = 0;
            if (
              warehouseDetails.FREIGHT_PRE_FEE &&
              warehouseDetails.FREIGHT_PRE_FEE > 0
            ) {
              basePrice = warehouseDetails.FREIGHT_PRE_FEE;
            } else if (rateOption.total_price && rateOption.total_price > 0) {
              basePrice = rateOption.total_price;
            } else if (warehouseDetails.PRICE && warehouseDetails.PRICE > 0) {
              basePrice = warehouseDetails.PRICE;
            } else if (warehouseDetails.price && warehouseDetails.price > 0) {
              basePrice = warehouseDetails.price;
            }

            // Calculate display price based on free freight logic
            let displayPrice = basePrice;
            if (shippingInfo?.free_freight && basePrice > 0) {
              // If free freight, show the difference between this rate and the preferred rate
              displayPrice = Math.max(0, basePrice - preferredRatePrice);
              if (displayPrice === 0) return null; // Skip if price is 0 after free freight adjustment, its probably the preferred rate
            }

            const isSelected =
              selectedRate &&
              selectedRate.type === rateType &&
              JSON.stringify(selectedRate.data) === JSON.stringify(rateOption);

            return (
              <button
                type="button"
                key={index}
                className={rateCardClasses(isSelected)}
                onClick={() => handleRateSelection(rateType, rateOption)}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="text-xl font-semibold tracking-tight text-gray-900">
                    ${displayPrice.toFixed(2) + " "}
                  </span>
                  {shippingInfo?.free_freight && (
                    <span className="text-sm text-gray-500">
                      - (<i>Includes ${preferredRatePrice} discount</i>)
                    </span>
                  )}
                </div>
                {/* Display fees for alternative rates */}
                {warehouseDetails.TOTAL_FEE_AMOUNT !== undefined && (
                  <div className="text-xs text-gray-600 mt-1">
                    {shippingInfo.free_freight
                      ? `Additional Fees: $${warehouseDetails.TOTAL_FEE_AMOUNT.toFixed(2)}`
                      : `Included Fees: $${warehouseDetails.TOTAL_FEE_AMOUNT.toFixed(2)}`}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gray-200 pt-3 text-sm text-gray-600">
                  <div>
                    Carrier: {service} ({carrier})
                  </div>
                  <div>Estimated Days: {days}</div>
                </div>
                {isSelected && (
                  <div className="mt-3 text-xs font-semibold text-[var(--primary-color)]">
                    ✓ Selected plan
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Function to force re-rating for testing
  const handleRedoRating = () => {
    // Reset selected rate
    setSelectedRate(null);
    // Reset total freight cost
    setTotalFreightCost(0);
    // Reset view states
    setShowAllRates(false);
    setShowSingleWarehouseRates(false);
    // Force re-dispatch of shipping info collection
    dispatch(collectShippingInfo());
  };

  return (
    <section className="shipping-container flex flex-col p-8">
      <header className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--primary-color)]">
            Freight desk · checkout step 3
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-gray-900">
            Choose how this order moves
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
            Live inventory, warehouse routing, and negotiated carrier rates in
            one plan.
          </p>
        </div>
        {window.location.href.includes("192.168.1.145") && (
          <button
            onClick={handleRedoRating}
            className="rounded-md bg-[var(--primary-color)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--secondary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={collectingShippingInfo}
          >
            {collectingShippingInfo ? "Rating..." : "Redo Rating"}
          </button>
        )}
      </header>

      {!collectingShippingInfo ? (
        <div>
          <>
            {shippingInfoError && (
              <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-red-300 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-700">
                  {shippingInfoError.message ||
                    "Shipping rates could not be loaded."}
                </p>
                <button
                  type="button"
                  onClick={handleRedoRating}
                  className="shrink-0 rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
            )}

            {shippingInfo.free_freight && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 rounded-md">
                <p className="text-green-700 font-medium">
                  Free freight available on preferred rate
                </p>
              </div>
            )}

            {/* Always render customer pickup option first */}
            {renderCustomerPickUp()}

            {/* Render preferred rate */}
            {renderPreferredRate()}

            {/* Toggle buttons */}
            <div className="mb-4 flex gap-3">
              {shippingInfo.all_rates && shippingInfo.all_rates.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowAllRates(!showAllRates);
                    setShowSingleWarehouseRates(false);
                  }}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2"
                >
                  {showAllRates ? "Hide More Rates" : "View More Rates"}
                </button>
              )}

              {shippingInfo.single_warehouse_rates &&
                shippingInfo.single_warehouse_rates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowSingleWarehouseRates(!showSingleWarehouseRates);
                      setShowAllRates(false);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2"
                  >
                    {showSingleWarehouseRates
                      ? "Hide"
                      : "View Single Rate Options"}
                  </button>
                )}
            </div>

            {/* Render alternative rates based on toggle state */}
            {showAllRates &&
              renderAlternativeRates(
                shippingInfo.all_rates,
                "all_rates",
                "All Available Rates",
              )}

            {showSingleWarehouseRates &&
              renderAlternativeRates(
                shippingInfo.single_warehouse_rates,
                "single_warehouse_rates",
                "Single Warehouse Rates",
              )}

            {!shippingInfoError &&
              !shippingInfo.preferred_rate &&
              (!shippingInfo.all_rates ||
                shippingInfo.all_rates.length === 0) &&
              (!shippingInfo.single_warehouse_rates ||
                shippingInfo.single_warehouse_rates.length === 0) && (
                <p className="text-gray-500">
                  No shipping rates available. You can select Customer Pick Up
                  above or it is safe to continue with your order.
                </p>
              )}

            {selectedRate && (
              <div className="mt-7 rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-[var(--primary-color)]">
                      Selected plan
                    </p>
                    <h3 className="mt-1 text-base font-medium text-gray-900">
                      Total freight cost
                    </h3>
                  </div>
                  <span className="text-2xl font-semibold tracking-tight text-gray-900">
                    {(totalFreightCost === 0 &&
                      shippingInfo.free_freight &&
                      selectedRate.type === "preferred_rate") ||
                    selectedRate.type === "customer_pickup"
                      ? "FREE"
                      : `$${totalFreightCost.toFixed(2)}`}
                  </span>
                </div>
                {shippingInfo.free_freight &&
                  selectedRate.type !== "preferred_rate" &&
                  selectedRate.type !== "customer_pickup" &&
                  totalFreightCost > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      This represents the difference between your selected
                      shipping and the free preferred rate.
                    </p>
                  )}
              </div>
            )}
          </>
        </div>
      ) : (
        <FreightLoading
          progress={shippingRatingProgress}
          status={shippingRatingStatus}
        />
      )}
    </section>
  );
}
